import React, { useState, useCallback } from "react";

const SOCIAL_PROVIDERS = [
  { key: "google", label: "Google", bg: "#DB4437", icon: "G" },
  { key: "facebook", label: "Facebook", bg: "#1877F2", icon: "f" },
  { key: "twitter", label: "X", bg: "#000000", icon: "\uD835\uDD4F" },
  { key: "discord", label: "Discord", bg: "#5865F2", icon: "D" },
] as const;

export function AuthForm({
  onClose,
  onLogin,
}: {
  onClose: () => void;
  onLogin: (token: string) => void;
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = mode === "login" ? "/api/auth/login" : "/api/auth/register";
      const body: Record<string, string> = { email, password };
      if (mode === "register" && displayName.trim()) {
        body.displayName = displayName.trim();
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        onLogin(data.token);
        onClose();
      } else {
        setError(data.error || "Something went wrong");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [mode, email, password, displayName, onLogin, onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(0,0,0,0.6)]" onClick={onClose}>
      <div
        className="bg-deep-navy border border-dark-gray rounded-[16px] p-8 max-w-sm w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display font-bold text-xl mb-6">
          {mode === "login" ? "Welcome back" : "Create account"}
        </h3>

        {/* Social login buttons */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {SOCIAL_PROVIDERS.map(({ key, label, bg, icon }) => (
            <a
              key={key}
              href={`/api/auth/${key}`}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-[10px] text-white text-xs font-display font-semibold transition-all duration-150 hover:-translate-y-[1px] hover:shadow-md"
              style={{ background: bg }}
            >
              <span className="text-sm font-bold">{icon}</span>
              {label}
            </a>
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-dark-gray" />
          <span className="text-[10px] font-mono tracking-[2px] uppercase text-mid-gray">or</span>
          <div className="flex-1 h-px bg-dark-gray" />
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "register" && (
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display name"
              className="w-full font-body text-sm bg-surface border border-dark-gray text-chalk px-4 py-2.5 rounded-[10px] outline-none transition-all duration-150 focus:border-electric-mint focus:shadow-[0_0_0_3px_rgba(0,240,160,0.1)] placeholder:text-mid-gray"
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full font-body text-sm bg-surface border border-dark-gray text-chalk px-4 py-2.5 rounded-[10px] outline-none transition-all duration-150 focus:border-electric-mint focus:shadow-[0_0_0_3px_rgba(0,240,160,0.1)] placeholder:text-mid-gray"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            minLength={6}
            className="w-full font-body text-sm bg-surface border border-dark-gray text-chalk px-4 py-2.5 rounded-[10px] outline-none transition-all duration-150 focus:border-electric-mint focus:shadow-[0_0_0_3px_rgba(0,240,160,0.1)] placeholder:text-mid-gray"
          />

          {error && <p className="text-neon-coral text-xs">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full font-display font-bold text-sm py-3 rounded-[10px] bg-electric-mint text-midnight hover:bg-[#33FFB8] transition-all duration-200 disabled:opacity-50"
          >
            {loading ? "..." : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>

        {/* Toggle mode */}
        <p className="text-center text-xs text-warm-gray mt-4">
          {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-electric-mint hover:underline"
          >
            {mode === "login" ? "Sign up" : "Log in"}
          </button>
        </p>
      </div>
    </div>
  );
}
